<template>
  <b-modal
    :title="helper.trans('labels.receipt_tg_bot_settings_title')"
    dialog-class="modal-lg"
    :modelValue="isOpen"
    body-class="pt-0"
    @update:modelValue="(open) => !open && cancel()"
  >
    <template #default>
      <div v-if="draft">
        <ul class="nav nav-tabs mt-2">
          <li class="nav-item">
            <a
              class="nav-link text-capitalize-first"
              :class="{ active: activeTab === ReceiptSettingsTab.forward }"
              href="#"
              @click.prevent="activeTab = ReceiptSettingsTab.forward"
            >
              {{ helper.trans('labels.receipt_forward_tab_classic') }}
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link text-capitalize-first"
              :class="{ active: activeTab === ReceiptSettingsTab.interactive }"
              href="#"
              @click.prevent="activeTab = ReceiptSettingsTab.interactive"
            >
              {{ helper.trans('labels.receipt_forward_tab_interactive') }}
            </a>
          </li>
        </ul>

        <div v-show="activeTab === ReceiptSettingsTab.forward" class="pt-2">
          <p class="text-muted small mb-3">{{ helper.trans('labels.receipt_forward_section_classic_lead') }}</p>

          <div class="row mt-2">
            <label for="tgReceiptForwardGroupId" class="col-sm-4 col-form-label text-capitalize-first">
              {{ helper.trans('labels.tg_receipt_forward_group_id') }}
            </label>
            <div class="col-sm-8">
              <FormInputWrapper v-slot="parentProps" :errors="getErrors('tgReceiptForwardGroupId')">
                <input
                  :id="'tgReceiptForwardGroupId'"
                  v-model="draft.tgReceiptForwardGroupId"
                  type="text"
                  class="form-control"
                  :class="parentProps.class"
                >
              </FormInputWrapper>
            </div>
          </div>

          <div class="row mt-2">
            <label for="receiptForwardMode" class="col-sm-4 col-form-label text-capitalize-first">
              {{ helper.trans('labels.receipt_forward_mode') }}
            </label>
            <div class="col-sm-8">
              <FormInputWrapper v-slot="parentProps" :errors="getErrors('receiptForwardMode')">
                <select
                  id="receiptForwardMode"
                  v-model="draft.receiptForwardMode"
                  class="form-select"
                  :class="parentProps.class"
                >
                  <option :value="ReceiptForwardMode.FULL">
                    {{ helper.trans('labels.receipt_forward_mode_full') }}
                  </option>
                  <option :value="ReceiptForwardMode.REDACTED">
                    {{ helper.trans('labels.receipt_forward_mode_redacted') }}
                  </option>
                </select>
              </FormInputWrapper>
            </div>
          </div>

          <div class="row mt-2">
            <label for="receiptForwardComment" class="col-sm-4 col-form-label text-capitalize-first">
              {{ helper.trans('labels.receipt_forward_comment') }}
            </label>
            <div class="col-sm-8">
              <FormInputWrapper v-slot="parentProps" :errors="getErrors('receiptForwardComment')">
                <textarea
                  id="receiptForwardComment"
                  v-model="draft.receiptForwardComment"
                  class="form-control"
                  :class="parentProps.class"
                  rows="3"
                ></textarea>
              </FormInputWrapper>
              <small class="text-muted">{{ helper.trans('labels.receipt_forward_comment_hint') }}</small>
            </div>
          </div>

          <div class="row mt-2">
            <label class="col-sm-4 col-form-label text-capitalize-first">
              {{ helper.trans('labels.receipt_forward_target_chats') }}
            </label>
            <div class="col-sm-8">
              <div class="mb-2">
                <div
                  v-for="(chatId, index) in draft.receiptForwardTargetChatIds"
                  :key="index"
                  class="d-flex flex-row align-items-center gap-2 mb-2"
                >
                  <FormInputWrapper
                    v-slot="parentProps"
                    :errors="getErrors(`receiptForwardTargetChatIds.${index}`)"
                    class="flex-grow-1"
                  >
                    <input
                      v-model="draft.receiptForwardTargetChatIds[index]"
                      type="text"
                      class="form-control"
                      :class="parentProps.class"
                      :placeholder="helper.trans('labels.receipt_forward_target_chat_placeholder', {number: index + 1})"
                    >
                  </FormInputWrapper>
                  <button
                    type="button"
                    class="btn btn-outline-danger btn-sm flex-shrink-0"
                    @click="removeTargetChat(index)"
                  >
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
                <button type="button" class="btn btn-outline-primary btn-sm" @click="addTargetChat">
                  <i class="fa fa-plus"></i> {{ helper.trans('labels.receipt_forward_add_chat') }}
                </button>
              </div>
              <small class="text-muted">{{ helper.trans('labels.receipt_forward_target_chats_hint') }}</small>
            </div>
          </div>

          <div v-if="draft.receiptForwardMode === ReceiptForwardMode.REDACTED" class="row mt-2">
            <label class="col-sm-4 col-form-label text-capitalize-first">
              {{ helper.trans('labels.receipt_forward_fields') }}
            </label>
            <div class="col-sm-8">
              <div
                v-for="field in RECEIPT_FORWARD_FIELD_CATALOG"
                :key="field.key"
                class="form-check small mb-1"
              >
                <input
                  class="form-check-input"
                  type="checkbox"
                  :value="field.key"
                  v-model="draft.receiptForwardFields"
                  :id="`receiptForwardField_${field.key}`"
                >
                <label class="form-check-label" :for="`receiptForwardField_${field.key}`">
                  {{ helper.trans(`labels.${field.label}`) }}
                </label>
              </div>
            </div>
          </div>
        </div>

        <div v-show="activeTab === ReceiptSettingsTab.interactive" class="pt-2">
          <p class="text-muted small mb-3">{{ helper.trans('labels.receipt_forward_section_interactive_lead') }}</p>

          <div class="row mt-2">
            <label for="tgReceiptActionsGroupId" class="col-sm-4 col-form-label text-capitalize-first">
              {{ helper.trans('labels.tg_receipt_actions_group_id') }}
            </label>
            <div class="col-sm-8">
              <FormInputWrapper v-slot="parentProps" :errors="getErrors('tgReceiptActionsGroupId')">
                <input
                  id="tgReceiptActionsGroupId"
                  v-model="draft.tgReceiptActionsGroupId"
                  type="text"
                  class="form-control"
                  :class="parentProps.class"
                >
              </FormInputWrapper>
              <small class="text-muted">{{ helper.trans('labels.tg_receipt_actions_group_id_hint') }}</small>
            </div>
          </div>

          <div class="row mt-2">
            <label class="col-sm-4 col-form-label text-capitalize-first">
              {{ helper.trans('labels.receipt_forward_actions') }}
            </label>
            <div class="col-sm-8">
              <div
                v-for="action in visibleReceiptActions"
                :key="action.key"
                class="form-check small mb-1"
              >
                <input
                  class="form-check-input"
                  type="checkbox"
                  :value="action.key"
                  v-model="draft.receiptForwardActions"
                  :id="`receiptForwardAction_${action.key}`"
                >
                <label class="form-check-label" :for="`receiptForwardAction_${action.key}`">
                  {{ helper.trans(`labels.${action.label}`) }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="d-flex justify-content-between w-100">
        <button type="button" class="btn btn-outline-secondary btn-sm text-capitalize-first" @click="cancel">
          {{ helper.trans('button.cancel') }}
        </button>
        <button type="button" class="btn btn-outline-primary btn-sm text-capitalize-first" @click="apply">
          {{ helper.trans('button.save') }}
        </button>
      </div>
    </template>
  </b-modal>
</template>

<script lang="ts">
import {computed, defineComponent, ref} from 'vue';
import {useStore} from 'vuex';
import {BModal} from 'bootstrap-vue-3';
import FormInputWrapper from '@/components/FormInputWrapper.vue';
import {helper} from '@/service/CommonHelper';
import {ReceiptForwardMode} from '@/models/enum/ReceiptForwardMode';
import ValidationErrorDTO from '@/models/ValidationErrorDTO';
import {
  MerchantReceiptForwardModalSettings,
  normalizeReceiptForwardModalSettings,
  RECEIPT_FORWARD_ACTION_CATALOG,
  RECEIPT_FORWARD_FIELD_CATALOG,
} from '@/service/merchantReceiptForward';

export type {MerchantReceiptForwardModalSettings};

enum ReceiptSettingsTab {
  forward = 'forward',
  interactive = 'interactive',
}

const isInteractiveReceiptError = (path: string): boolean =>
  path === 'tgReceiptActionsGroupId'
  || path.startsWith('tgReceiptActionsGroupId.')
  || path === 'receiptForwardActions'
  || path.startsWith('receiptForwardActions.');

export default defineComponent({
  components: {
    BModal,
    FormInputWrapper,
  },
  emits: ['apply'],
  setup(_, {emit}) {
    const store = useStore();
    const isOpen = ref(false);
    const activeTab = ref<ReceiptSettingsTab>(ReceiptSettingsTab.forward);
    const draft = ref<MerchantReceiptForwardModalSettings | null>(null);
    const snapshot = ref<MerchantReceiptForwardModalSettings | null>(null);
    const errors = ref<Record<string, ValidationErrorDTO[]>>({});

    const cloneSettings = (settings: MerchantReceiptForwardModalSettings): MerchantReceiptForwardModalSettings =>
      normalizeReceiptForwardModalSettings(settings);

    const show = (settings: MerchantReceiptForwardModalSettings, validationErrors: Record<string, ValidationErrorDTO[]> = {}) => {
      const cloned = cloneSettings(settings);
      draft.value = cloned;
      snapshot.value = cloneSettings(cloned);
      errors.value = validationErrors;
      activeTab.value = Object.keys(validationErrors).some(isInteractiveReceiptError)
        ? ReceiptSettingsTab.interactive
        : ReceiptSettingsTab.forward;
      isOpen.value = true;
    };

    const hide = () => {
      isOpen.value = false;
      draft.value = null;
      snapshot.value = null;
      errors.value = {};
    };

    const cancel = () => {
      hide();
    };

    const apply = () => {
      if (!draft.value) {
        return;
      }
      emit('apply', cloneSettings(draft.value));
      hide();
    };

    const addTargetChat = () => {
      draft.value?.receiptForwardTargetChatIds.push('');
    };

    const removeTargetChat = (index: number) => {
      draft.value?.receiptForwardTargetChatIds.splice(index, 1);
    };

    const getErrors = (path: string) => errors.value[path] ?? [];

    const chequeCheckerEnabled = computed(() => !!store.getters.getAppData?.CHEQUE_WALES_CHECKER_ENABLED);

    const visibleReceiptActions = computed(() =>
      RECEIPT_FORWARD_ACTION_CATALOG.filter((action) =>
        action.key !== 'check_receipt' || chequeCheckerEnabled.value,
      ),
    );

    return {
      isOpen,
      activeTab,
      ReceiptSettingsTab,
      draft,
      helper,
      ReceiptForwardMode,
      RECEIPT_FORWARD_FIELD_CATALOG,
      visibleReceiptActions,
      show,
      hide,
      cancel,
      apply,
      addTargetChat,
      removeTargetChat,
      getErrors,
    };
  },
});
</script>
